import { FinancialStatementAccount } from 'src/app/domain/financial-statement/models/common/FinancialStatementAccount';
import { GeneralLedgerTransaction } from 'src/app/domain/financial-statement/models/common/GeneralLedgerTransaction';
import { FinancialStatement } from '../financial-statement/models/common/FinancialStatement';
import type { RoundsByCompaniesNames, Rounds } from './templates/companies-rounds-by-period/interfaces';
import * as XLSX from 'xlsx-js-style';
import { CashOnCashExport } from 'src/app/domain/cashflow/dto/CashOnCashExport';
import { ExportExcelOverview } from './templates/portfolio-overview/interfaces';
import { Company } from 'src/app/domain/pp/dto/Company';
import { RoundTableExcel } from 'src/app/domain/company-performance/round/round-table/round-table.component';
import { FundPerformance } from 'src/app/domain/fund-performance/dto/FundPerformance';
import { CommitmentAllocation } from './templates/commitment-allocation/commitment-allocation.template';
import { PeriodComparisonExportData } from './templates/comparison/period-comparison';
import { CapitalAccountData } from '../financial-statement/utils/capital-account/utils';
import { Employee } from '../organization/dto/Employee';
import { InitialAndFollowOnPortfolios } from '../pp/service/PortfolioInitialAndFollowOnCalculation';
import { Overview } from '../organization/components/org-pp-funds-overview/org-pp-funds-overview.component';
import { BsExcelExportData } from 'src/app/services/balance-sheet/bsTemplate';
import { PlExcelExportData } from './templates/financial-statement/profit-and-loss/profitAndLossTemplate';
import { ComparisonByValueWrapper } from './templates/comparison-by/comparison-by-values';
import { RoundsComparisonExportExcelDTO } from './templates/round-comparison/round-comparison-template';
import { FsExportExcelData } from '../financial-statement/utils/common/utilityClasses';
import { CommitmentAllocationModelExcelExport } from '../follow-on/component/commitment-allocation-model-table/commitment-allocation-model-table.component';
import { AppreciationAtCostExcelWrapper } from '../pp/component/sub/appreciation-at-cost/appreciation-at-cost.component';
import { FundsAndPerformanceExportExcelWrapper } from '../organization/components/organization-overview/utils';
import { GroupedPortfolioExcelWrapper }
  from '../organization/components/fair-value-and-pp-charts/pojo/fmv-charts-classes';
import { InitialFollowOnTableDTO } from '../pp/component/sub/initial-follow-on-investment/pojo/initial-follow-on-classes';
import { CashFlow } from '../company-performance/cash-flow/dto/CashFlow';
import { MarketAnalysisByVintageChartDTO } from '../market-analysis/dto/MaByIndustryChartDTO';
import { MaFundsSettingsExportExcelData }
  from '../recovery/component/recovery-market-analysis/ma-funds-settings/ma-funds-settings.component';
import { MaRound } from '../recovery/component/recovery-market-analysis/pojo/ma-classes';
import { PerformanceMA } from '../recovery/component/recovery-market-analysis/dto/PerformanceMA';
import {
  MetricPortfolioAnalysisWrapperCompanyExportExcel,
} from "../kpi/components/kpi-metrics-main/portfolio-analysis/kpi-portfolio-analysis.component";
import {
  MetricCrossCompanyExportExcel,
} from "../kpi/components/kpi-metrics-main/cross-company/kpi-cross-company.component";
import { FundStrategyExportExcelData, GroupFS, RoundsFS } from '../fund-strategy/pojo/fund-strategy-export-classes';
import { FsGroupFormGroup } from '../fund-strategy/pojo/fund-strategy-export-types';
import { EvaluationDataForExcel } from '../company-performance/business-evaluation/dto/EvaluationDataForExcel';
import { HideableRecoveryUserDTO } from '../recovery/dto/RecoveryUserDTO';
import { CompanyWithRoundsDTO } from '../company/dto/CompanyWithRoundsDTO';
import {
  NetIrrWrapper,
} from '../fund-performance/component/fund-performances/fund-performance-card/fund-performance-card.component';
import { CompanyComparableValuationExcelExportData } from '../company-performance/companies-stocks/dto/StockAuxiliary';
import { Fund } from '../fund-performance/dto/Fund';
import { AllSoiForOrg, CompanyWithSoiForFund, FundSoiLists, SoiAllPeriodsLists } from '../soi/dto/SoIAuxiliary';

export enum ExcelTemplateName {
  allCompaniesRoundsByPeriod,
  singleCompanyRoundsByPeriod,
  cashOnCash,
  overview,
  companies,
  assembly,
  gl,
  tb,
  financialStatement,
  capitalAccount,
  fundPerformance,
  commitmentAllocation,
  cashFlowTemplate,
  investorContributionTemplate,
  periodComparisonTemplate,
  initialFollowOnInvestmentTemplate,
  companyMembersTemplate,
  ppByFundTemplate,
  balanceSheetTemplate,
  profitAndLossTemplate,
  sharedDetails,
  valuationBasis,
  comparisonByValue,
  roundComparison,
  followOnModelTableTemplateStepThree,
  followOnModelTableStepTwo,
  followOnModelAllSteps,
  ppAnalysisAtCost,
  fundAndPerformance,
  dataBehindFvOpChart,
  cashFlowDealFLow,
  maVintageTable,
  maFundsSettings,
  maAllRounds,
  maByCompany,
  maByIndustry,
  fpActiveTable,
  KpiPortfolioAnalysis,
  KpiCrossCompany,
  fundStrategyInternal,
  fundStrategyExternal,
  companyComparableValuation,
  businessEvaluation,
  usersRecovery,
  funds,
  soiByFund,
  soiByOrg,
  soiAllPeriods,
}

export type ExcelData =
  RoundsByCompaniesNames |
  Rounds |
  RoundTableExcel |
  CashOnCashExport |
  FundPerformance[] |
  CompanyWithSoiForFund[] |
  ExportExcelOverview |
  Company[] |
  CompanyWithRoundsDTO [] |
  FinancialStatement |
  GeneralLedgerTransaction[] |
  Map<string, GeneralLedgerTransaction[]> |
  Map<string, GeneralLedgerTransaction[]>[] |
  FinancialStatementAccount[] |
  CapitalAccountData |
  CommitmentAllocation |
  CashFlow[] |
  NetIrrWrapper |
  PeriodComparisonExportData |
  InitialAndFollowOnPortfolios |
  Employee[] |
  Overview[] |
  BsExcelExportData |
  PlExcelExportData |
  ComparisonByValueWrapper |
  RoundsComparisonExportExcelDTO |
  FsExportExcelData |
  CommitmentAllocationModelExcelExport |
  FundsAndPerformanceExportExcelWrapper |
  AppreciationAtCostExcelWrapper |
  GroupedPortfolioExcelWrapper |
  InitialFollowOnTableDTO |
  MarketAnalysisByVintageChartDTO |
  MaFundsSettingsExportExcelData |
  MaRound[] |
  PerformanceMA[] |
  MetricPortfolioAnalysisWrapperCompanyExportExcel |
  MetricCrossCompanyExportExcel |
  RoundsFS[] |
  GroupFS[] |
  FsGroupFormGroup |
  CompanyComparableValuationExcelExportData |
  EvaluationDataForExcel |
  HideableRecoveryUserDTO[] |
  Fund[] |
  FundStrategyExportExcelData | FundSoiLists |
  AllSoiForOrg | SoiAllPeriodsLists;

export interface ExcelOptions {
  isAdmin?: boolean;
  soiEntireOrg?: boolean;
  isQuarterlyGl?: boolean;
}

export interface ExcelTemplate {
  /**
   * Main method that exports data to Excel format.
   */
  export(data: ExcelData, options: ExcelOptions): XLSX.WorkBook;
}

export type Row = unknown[];
export type Rows = Row[];
